import { FormProps } from '@alphabait/ui/form'
import { Control, useController } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import i18n from '../../i18n'
import { Switch } from '../switch'
import { AlphaTypes, CustomSwitchInput } from './custom-types'
import styles from './form.module.css'

// Legacy mode props
type LegacyProps = {
  field: CustomSwitchInput
  formProps: FormProps
}

// Direct mode props (react-hook-form)
type DirectProps = {
  name: string
  control: Control<any>
  defaultValue?: boolean
  disabled?: boolean
  // When provided, the toggle is handed to the parent instead of writing to
  // the form directly — lets a caller confirm a risky change before it sticks.
  onCheckedChange?: (value: boolean) => void
}

type CustomSwitchProps = LegacyProps | DirectProps

function isLegacyProps(props: CustomSwitchProps): props is LegacyProps {
  return 'field' in props && 'formProps' in props
}

export function CustomSwitchComponent(props: CustomSwitchProps) {
  // Extract props based on mode
  const fieldName = isLegacyProps(props) ? props.field.name : props.name
  const control = isLegacyProps(props) ? props.formProps.control : props.control
  const defaultValue = isLegacyProps(props)
    ? false
    : props.defaultValue ?? false
  const disabled = isLegacyProps(props)
    ? props.field.uiOptions?.disabled
    : props.disabled
  const onCheckedChange = isLegacyProps(props)
    ? undefined
    : props.onCheckedChange

  const {
    field: controllerField,
    fieldState: { error },
  } = useController({
    control,
    name: fieldName,
    defaultValue,
  })

  const { t } = useTranslation()

  return (
    <div className="flex flex-col gap-1 py-2 w-full">
      <div className="flex flex-row items-center justify-between gap-4 my-2 text-slate-50">
        <Switch
          onCheckedChange={onCheckedChange ?? controllerField.onChange}
          checked={controllerField.value}
          disabled={disabled}
        />
      </div>
      {error && (
        <span
          className={styles.errorMessage}
          data-testid={`${fieldName}-error`}
        >
          {t(`validation_messages.${error.message}`)}
        </span>
      )}
    </div>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export const getCustomSwitchProperties = () =>
  [
    {
      name: 'uiOptions.label',
      type: 'input',
      uiOptions: {
        label: i18n.t('questionnaire_builder.question'),
        inputType: 'text',
      },
      validations: ['required'],
    },
    {
      name: 'validation.required',
      customControlType: 'switch',
      type: 'custom',
      uiOptions: {
        label: i18n.t('questionnaire_builder.required'),
      },
    },
  ] as AlphaTypes[]
